import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import NavigationTracker from '@/components/NavigationTracker';
import AppShell from '@/components/layout/AppShell';
import { RequireAuth } from '@/components/auth/RequireAuth';
import MasterdataWorkbenchRouteGuard from '@/components/masterdata/MasterdataWorkbenchRouteGuard';
import { Loader } from '@/components/ui/loader';
import { HelpModal, showHelp } from './help';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const ProjectsPage = lazy(() => import('@/pages/project/ProjectsPage'));
const ProjectDetailsPage = lazy(() => import('@/pages/project/[id]'));
const ProjectFormPage = lazy(() => import('@/pages/project/ProjectFormPage'));
const OrganizationPage = lazy(() => import('@/pages/project/OrganizationPage'));
const InspectionTemplatesPage = lazy(
  () => import('@/pages/project/InspectionTemplatesPage'),
);
const CompanyEditor = lazy(() => import('@/pages/settings/CompanyTab'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const CustomersPage = lazy(() => import('@/pages/CustomerPage'));
const VendorsPage = lazy(() => import('@/pages/VendorPage'));
const VendorFormPage = lazy(() => import('@/pages/VendorFormPage'));
const VendorAssessmentPage = lazy(() => import('@/pages/VendorAssessmentPage'));
const ContactsPage = lazy(() => import('@/pages/ContactsPage'));
const ContactFormPage = lazy(() => import('@/pages/ContactFormPage'));
const DocumentLibraryPage = lazy(() => import('@/pages/DocumentLibraryPage'));
const TimeEntryPage = lazy(() => import('@/pages/time/TimeEntryPage'));
const TimeOverviewPage = lazy(() => import('@/pages/time/TimeOverviewPage'));
const TimeApprovalPage = lazy(() => import('@/pages/time/TimeApprovalPage'));
const UserFormPage = lazy(() => import('./pages/UserFormPage'));
const EmployeeFormPage = lazy(() => import('./pages/EmployeeFormPage'));
const UsersTablePage = lazy(() => import('./pages/UsersTablePage'));
const EmployeesTablePage = lazy(() => import('./pages/EmployeesTablePage'));
const WageTypePage = lazy(() => import('@/pages/time/WageTypePage'));
const ChartOfAccountPage = lazy(() => import('@/pages/ChartOfAccountPage'));
const VatTypePage = lazy(() => import('@/pages/VatTypePage'));
const ResourceTypePage = lazy(() => import('@/pages/ResourceTypePage'));
const ResourceGroupPage = lazy(() => import('@/pages/ResourceGroupPage'));
const ResourcesPage = lazy(() => import('@/pages/ResourcePage'));
const UnitPage = lazy(() => import('@/pages/UnitPage'));
const EmployeePlannerPage = lazy(() => import('@/pages/EmployeePlannerPage'));
const ResourcePlannerPage = lazy(() => import('@/pages/ResourcePlannerPage'));
const PlanningBoardPage = lazy(() => import('@/pages/PlanningBoardPage'));
const ProjectSchedulePortfolioPage = lazy(() => import('@/pages/ProjectSchedulePortfolioPage'));
const ShiftPlannerPage = lazy(() => import('@/pages/ShiftPlannerPage'));
const ShiftTemplatesPage = lazy(() => import('@/pages/ShiftTemplatesPage'));
const CustomerFormPage = lazy(() => import('@/pages/CutomerFormPage'));
const StaffLogsAdminPage = lazy(() => import('@/pages/StaffLogsAdminPage'));
const ProjectsAdminPage = lazy(() => import('@/pages/ProjectsAdminPage'));
const SessionAuditPage = lazy(() => import('@/pages/SessionAuditPage'));
const HelpPage = lazy(() => import('@/pages/HelpPage'));
const FeedbackPage = lazy(() => import('@/pages/FeedbackPage'));
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'));
const ChangelogPage = lazy(() => import('@/pages/ChangelogPage'));
const CategoriesPage = lazy(() => import('@/pages/CategoriesPage'));
const EmailTemplatesPage = lazy(() => import('@/pages/EmailTemplatesPage'));
const EmailLogsPage = lazy(() => import('@/pages/EmailLogsPage'));
const EmailSettingsPage = lazy(() => import('@/pages/EmailSettingsPage'));
const TimeEntrySettingsPage = lazy(
  () => import('@/pages/TimeEntrySettingsPage'),
);
const ClassificationCodesPage = lazy(
  () => import('@/pages/ClassificationCodesPage'),
);
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const CheckInPage = lazy(() => import('@/pages/CheckInPage'));
const ChemicalManagementPage = lazy(
  () => import('@/pages/chemicals/ChemicalManagementPage'),
);
const ChemicalProductPublicPage = lazy(
  () => import('@/pages/chemicals/ChemicalProductPublicPage'),
);
const PermitsListPage = lazy(() => import('@/pages/permits/PermitsListPage'));
const PermitCreatePage = lazy(() => import('@/pages/permits/PermitCreatePage'));
const PermitDetailPage = lazy(() => import('@/pages/permits/PermitDetailPage'));
const InvoiceBasePage = lazy(() => import('@/pages/invoice/InvoiceBasePage'));
const FinancePage = lazy(() => import('@/pages/FinancePage'));
const BudgetsPage = lazy(() => import('@/pages/BudgetsPage'));
const OngoingWorkPage = lazy(() => import('@/pages/OngoingWorkPage'));
const CalculationsPage = lazy(() => import('@/pages/CalculationsPage'));
const PurchasesPage = lazy(() => import('@/pages/PurchasesPage'));
const ItemsPage = lazy(() => import('@/pages/ItemsPage'));
const LeaveRequestPage = lazy(() => import('@/pages/leave/LeaveRequestPage'));
const LeaveApprovalsPage = lazy(
  () => import('@/pages/leave/LeaveApprovalsPage'),
);
const LeaveCalendarPage = lazy(
  () => import('@/pages/leave/LeaveCalendarPage'),
);
const LeaveSeasonsPage = lazy(
  () => import('@/pages/leave/LeaveSeasonsPage'),
);
const LeaveBalancesPage = lazy(
  () => import('@/pages/leave/LeaveBalancesPage'),
);
const LeaveDelegationsPage = lazy(
  () => import('@/pages/leave/LeaveDelegationsPage'),
);
const LeaveBlackoutsPage = lazy(
  () => import('@/pages/leave/LeaveBlackoutsPage'),
);
const LeaveWageTypeMappingsPage = lazy(
  () => import('@/pages/leave/LeaveWageTypeMappingsPage'),
);
const PurchaseOrderPage = lazy(() => import('@/pages/PurchaseOrderPage'));
const SharePointDebugPage = lazy(
  () => import('@/pages/admin/SharePointDebugPage'),
);
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const ShareScheduleViewer = lazy(
  () => import('@/pages/project/ShareScheduleViewer'),
);

export default function App() {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'F1' || event.code === 'F1' || event.key === 'Help') {
        event.preventDefault();
        showHelp(window.location.pathname);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <BrowserRouter>
      <NavigationTracker />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/checkin" element={<CheckInPage />} />
          <Route
            path="/chemical-products/:slug"
            element={<ChemicalProductPublicPage />}
          />
          <Route path="/share/schedule/:token" element={<ShareScheduleViewer />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route
            path="*"
            element={
              <RequireAuth>
                <AppShell>
                  <Suspense fallback={<Loader />}>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/projects" element={<ProjectsPage />} />
                      <Route
                        path="/documents"
                        element={<DocumentLibraryPage />}
                      />
                      <Route
                        path="/projects/inspection-templates"
                        element={<InspectionTemplatesPage />}
                      />
                      <Route
                        path="/chemicals"
                        element={<ChemicalManagementPage />}
                      />
                      <Route path="/permits" element={<PermitsListPage />} />
                      <Route path="/permits/new" element={<PermitCreatePage />} />
                      <Route path="/permits/:id" element={<PermitDetailPage />} />
                      <Route path="/search" element={<SearchPage />} />
                      <Route
                        path="/projects/new"
                        element={<ProjectFormPage />}
                      />
                      <Route
                        path="/projects/:id"
                        element={<ProjectFormPage />}
                      />
                      <Route
                        path="/project/:id"
                        element={<ProjectDetailsPage />}
                      />
                      <Route
                        path="/projects/:id/organization"
                        element={<OrganizationPage />}
                      />
                      <Route path="/customers" element={<CustomersPage />} />
                      <Route
                        path="/customers/new"
                        element={
                          <MasterdataWorkbenchRouteGuard backPath="/customers">
                            <CustomerFormPage />
                          </MasterdataWorkbenchRouteGuard>
                        }
                      />
                      <Route
                        path="/customers/:id"
                        element={<CustomerFormPage />}
                      />
                      <Route path="/company" element={<CompanyEditor />} />
                      <Route path="/settings" element={<SettingsPage />} />
                      <Route
                        path="/settings/projectfolders"
                        element={<SettingsPage initialTab="projectfolders" />}
                      />
                      <Route path="/categories" element={<CategoriesPage />} />
                      <Route
                        path="/classification-codes"
                        element={<ClassificationCodesPage />}
                      />
                      <Route path="/vendors" element={<VendorsPage />} />
                      <Route
                        path="/vendors/new"
                        element={
                          <MasterdataWorkbenchRouteGuard backPath="/vendors">
                            <VendorFormPage />
                          </MasterdataWorkbenchRouteGuard>
                        }
                      />
                      <Route
                        path="/vendors/:id"
                        element={<VendorFormPage />}
                      />
                      <Route
                        path="/vendors/:id/assessments"
                        element={<VendorAssessmentPage />}
                      />
                      <Route path="/contacts" element={<ContactsPage />} />
                      <Route
                        path="/contacts/new"
                        element={
                          <MasterdataWorkbenchRouteGuard backPath="/contacts">
                            <ContactFormPage />
                          </MasterdataWorkbenchRouteGuard>
                        }
                      />
                      <Route
                        path="/contacts/:id"
                        element={<ContactFormPage />}
                      />
                      <Route path="/time" element={<TimeEntryPage />} />
                      <Route
                        path="/time/overview"
                        element={<TimeOverviewPage />}
                      />
                      <Route
                        path="/time/approval"
                        element={<TimeApprovalPage />}
                      />
                      <Route
                        path="/invoicebases/:id"
                        element={<InvoiceBasePage />}
                      />
                      <Route path="/users" element={<UsersTablePage />} />
                      <Route
                        path="/users/new"
                        element={
                          <MasterdataWorkbenchRouteGuard backPath="/users">
                            <UserFormPage />
                          </MasterdataWorkbenchRouteGuard>
                        }
                      />
                      <Route
                        path="/users/:id"
                        element={<UserFormPage />}
                      />
                      <Route
                        path="/employees"
                        element={<EmployeesTablePage />}
                      />
                      <Route
                        path="/employees/new"
                        element={
                          <MasterdataWorkbenchRouteGuard backPath="/employees">
                            <EmployeeFormPage />
                          </MasterdataWorkbenchRouteGuard>
                        }
                      />
                      <Route
                        path="/employees/:id"
                        element={<EmployeeFormPage />}
                      />
                      <Route path="/wagetypes" element={<WageTypePage />} />
                      <Route
                        path="/chartofaccounts"
                        element={<ChartOfAccountPage />}
                      />
                      <Route path="/vattypes" element={<VatTypePage />} />
                      <Route
                        path="/resourcetypes"
                        element={<ResourceTypePage />}
                      />
                      <Route
                        path="/resourcegroups"
                        element={<ResourceGroupPage />}
                      />
                      <Route path="/units" element={<UnitPage />} />
                      <Route path="/resources" element={<ResourcesPage />} />
                      <Route path="/finance" element={<FinancePage />} />
                      <Route path="/budgets" element={<BudgetsPage />} />
                      <Route
                        path="/finance/ongoing-work"
                        element={<OngoingWorkPage />}
                      />
                      <Route
                        path="/calculations"
                        element={<CalculationsPage />}
                      />
                      <Route path="/leave" element={<LeaveRequestPage />} />
                      <Route
                        path="/leave/approvals"
                        element={<LeaveApprovalsPage />}
                      />
                      <Route
                        path="/leave/calendar"
                        element={<LeaveCalendarPage />}
                      />
                      <Route
                        path="/leave/seasons"
                        element={<LeaveSeasonsPage />}
                      />
                      <Route
                        path="/leave/balances"
                        element={<LeaveBalancesPage />}
                      />
                      <Route
                        path="/leave/delegations"
                        element={<LeaveDelegationsPage />}
                      />
                      <Route
                        path="/leave/blackouts"
                        element={<LeaveBlackoutsPage />}
                      />
                      <Route
                        path="/leave/wage-types"
                        element={<LeaveWageTypeMappingsPage />}
                      />
                      <Route path="/purchases" element={<PurchasesPage />} />
                      <Route path="/items" element={<ItemsPage />} />
                      <Route
                        path="/purchaseorders/:id"
                        element={<PurchaseOrderPage />}
                      />
                      <Route
                        path="/planning/overview"
                        element={<PlanningBoardPage />}
                      />
                      <Route
                        path="/planning/schedules"
                        element={<ProjectSchedulePortfolioPage />}
                      />
                      <Route
                        path="/planning/shifts"
                        element={<ShiftPlannerPage />}
                      />
                      <Route
                        path="/planning/shifts/templates"
                        element={<ShiftTemplatesPage />}
                      />
                      <Route
                        path="/planner"
                        element={<EmployeePlannerPage />}
                      />
                      <Route
                        path="/resourceplanner"
                        element={<ResourcePlannerPage />}
                      />
                      <Route
                        path="/stafflogs"
                        element={<StaffLogsAdminPage />}
                      />
                      <Route
                        path="/projectfolders"
                        element={<ProjectsAdminPage />}
                      />
                      <Route
                        path="/session-audit"
                        element={<SessionAuditPage />}
                      />
                      <Route
                        path="/admin/sharepoint-debug"
                        element={<SharePointDebugPage />}
                      />
                      <Route
                        path="/emailsettings"
                        element={<EmailSettingsPage />}
                      />
                      <Route
                        path="/timeentrysettings"
                        element={<TimeEntrySettingsPage />}
                      />
                      <Route
                        path="/emailtemplates"
                        element={<EmailTemplatesPage />}
                      />
                      <Route path="/emaillogs" element={<EmailLogsPage />} />
                      <Route path="/help" element={<HelpPage />} />
                      <Route path="/changelog" element={<ChangelogPage />} />
                      <Route path="/feedback" element={<FeedbackPage />} />
                    </Routes>
                  </Suspense>
                </AppShell>
              </RequireAuth>
            }
          />
        </Routes>
      </Suspense>
      <HelpModal />
    </BrowserRouter>
  );
}
